import * as React from 'react';
import {COLORS} from '../styles';
import {SystemIcons} from '../icons';
import {LoadingIndicator} from '../LoadingIndicator';
import {DropdownContainer, DropdownInputFieldStyles, DropdownInputField} from './CommonStyling';
import DropdownContent, {DropdownItem} from './DropdownContent';
import {AutofilledMessage, ErrorMessage} from '../InputFields';
import TooltipOverflow from '../Tooltips/TooltipOverflow';
import {Size, Testable} from '../types';
import {defaultOnMouseDownHandler, useClickOutsideRef, useFocusVisibleRef} from '../common';
import {useFocusOutsideRef} from '../common';

export interface BasicDropdownCommonProps extends Testable, Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect' | 'placeholder' | 'tabIndex' | 'onMouseDown' | 'onClick' | 'onKeyDown'>{
  list: DropdownItem[];
  itemsType?: 'normal' | 'checkbox' | 'radio';
  action?: () => void;
  actionLabel?: string;
  actionVariant?: 'primary' | 'secondary' | 'tertiary' | undefined;
  actionIcon?: React.ReactNode;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  pinTopItem?: boolean;
  scrollable?: boolean;
  maxHeight?: string;
  placeholder?: string;
  disableSorting?: boolean;
  messageOnNoResults?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isButton?: boolean;
  activeValidationMessage?: string;
  autofilledMessage?: string;
  size?: Size.Small | Size.Medium;
  margin?: string;
  minWidth?: string;
  textOverflow?: 'ellipsis' | 'default';
  overflowTooltipPosition?: 'top' | 'bottom';
}

export type BasicDropdownMultiSelectProps = BasicDropdownCommonProps & {
  multiSelect: true;
  value?: string[];
  onSelect: (value: string[]) => void;
}

export type BasucDropdownSingleSelectProps = BasicDropdownCommonProps & {
  multiSelect?: false;
  value?: string;
  onSelect: (value: string) => void;
}

export type BasicDropdownProps = BasucDropdownSingleSelectProps | BasicDropdownMultiSelectProps;

const BasicDropdown = React.forwardRef((props: BasicDropdownProps, ref) => {
  const {
    id,
    list,
    placeholder,
    messageOnNoResults,
    itemsType = 'normal',
    action,
    actionLabel,
    actionVariant,
    actionIcon,
    actionLoading,
    actionDisabled,
    pinTopItem,
    scrollable = true,
    maxHeight,
    disabled,
    readOnly,
    isButton,
    activeValidationMessage,
    autofilledMessage,
    size,
    margin,
    minWidth,
    className,
    overflowTooltipPosition,
    onBlur,
    dataTestId,
    onSelect,
    textOverflow = 'ellipsis',
    ...rest
  } = props;

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [activeDescendant, setActiveDescendant] = React.useState<string>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [input, setInput] = React.useState<string>('');
  const [focused, setFocused] = React.useState<number | null>(null);
  
  const [keyboardNavigated, setKeyboardNavigated] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const styledFieldRef = useFocusVisibleRef([inputRef]);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const resetDropdown = () => {
    setIsOpen(false);
    setFocused(null);
  };

  const containerRef = useFocusOutsideRef(resetDropdown, [dropdownRef]);

  useClickOutsideRef(resetDropdown, [], containerRef);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(!isOpen);
      setKeyboardNavigated(true);
    }
  };

  const handleValueSelect = (values: string[]) => {
    setInput(values.join(', '));

    if (props.multiSelect === true) {
      props.onSelect(values);
    } else if (props.multiSelect === false || props.multiSelect === undefined) {
      props.onSelect(values[0]);
    }

    //don't close dropdown on item select if have custom action or multiselect
    if (actionLabel || props.multiSelect) return;

    if (keyboardNavigated) {
      styledFieldRef.current?.focus({focusVisible: true} as any);
    }
    setIsOpen(false);
    setFocused(null);
  };

  React.useEffect(() => {
    if (props.value) {
      if (props.multiSelect) {
        setInput(props.value.join(', '));
      } else if (props.multiSelect === false || props.multiSelect === undefined) {
        setInput(props.value);
      }
    } else {
      setInput('');
    }
  }, [props.value, props.multiSelect]);

  React.useEffect(() => {
    setIsLoading(false);
  }, [input]);

  React.useEffect(() => {
    !isOpen && setKeyboardNavigated(false);
  }, [isOpen]);

  React.useImperativeHandle(ref, () => inputRef.current, [inputRef]);

  const customSetIsOpen = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const getDisplayItems = () => {
    const values = input.split(',').map((val) => val.trim());
    const listDisplayLabels = list.filter((item) => values.includes(item.value)).map((l) => l.displayLabel);
    return listDisplayLabels.join(', ');
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // @ts-ignore
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onBlur && onBlur(e);
    }
  };

  const cls = (isOpen ? 'expanded ' : '').concat(size ? size : '').concat(isButton ? ' button ' : '').concat(className ? ` ${className}` : '');
  
  return (
    <>
      <DropdownContainer ref={containerRef}
                aria-expanded={isOpen ? true : false}
                aria-activedescendant={activeDescendant}
                aria-controls={`${id}_dropdowncontent`}
                role="listbox"
                aria-multiselectable={props.multiSelect}
                isButton={isButton || false}
                className={size ? size : ''}
                readOnly={readOnly}
                disabled={disabled}
                margin={margin}
                onBlur={handleBlur}
                minWidth={minWidth}>
        <DropdownInputFieldStyles
          ref={styledFieldRef}
          className={cls}
          onMouseDown={defaultOnMouseDownHandler}
          onClick={(e) => {
            if (!readOnly && !disabled) {
              e.stopPropagation();
              setIsOpen(!isOpen);
              if (isOpen) {
                inputRef.current?.blur();
              } else {
                inputRef.current?.focus();
              }
            }
          }}
          tabIndex={disabled || readOnly ? -1 : 0}
          disabled={disabled || false}
          readOnly={readOnly || false}
          isPlaceholder={!input}
          showValidationMessage={!!activeValidationMessage}
          onKeyDown={handleKeyDown}
          minWidth={minWidth}
          {...rest}>
          <DropdownInputField
            ref={inputRef}
            type="search"
            ellipsis={textOverflow == 'ellipsis'}
            id={id}
            data-testid={dataTestId}
            readOnly
            placeholder={placeholder}
            value={getDisplayItems()}
            className={size ? `${size} value` : 'value'}
            tabIndex={-1}
            disabled={disabled || false}
          />
          {!isOpen ? <TooltipOverflow position={overflowTooltipPosition} input={inputRef} withArrow={true} maxWidth="100%" size={size} align='end'>
            {getDisplayItems()}
        </TooltipOverflow> : null}
          {isLoading ? <LoadingIndicator size={Size.Small} color={COLORS.neutral_600}/> : null}
          <div className={'icon dropdown-arrow'}>
            {isOpen ? <SystemIcons.ArrowDropUp size="24px" className={size ? size : ''}/> :
              <SystemIcons.ArrowDropDown size="24px" className={size ? size : ''}/>}
          </div>
        </DropdownInputFieldStyles>
        {!readOnly && !disabled && (
          <DropdownContent
            ref={dropdownRef}
            containerRef={containerRef}
            onActiveDescendantChanged={(id) => setActiveDescendant(id)}
            customizationProps={{
              itemsType: itemsType,
              action: action ?? (() => {
              }),
              actionLabel: actionLabel,
              actionVariant: actionVariant,
              actionDisabled: actionDisabled,
              scrollable: scrollable,
              onValueUpdate: handleValueSelect,
              items: list,
              multiSelect: props.multiSelect,
              pinTopItem: pinTopItem,
              maxHeight: maxHeight,
              actionIcon: actionIcon,
              actionLoading: actionLoading,
            }}
            ariaRolesType="input"
            focused={focused}
            setFocused={setFocused}
            size={size ?? Size.Small}
            filter={''}
            isOpen={isOpen}
            setIsOpen={customSetIsOpen}
            messageOnNoResults={messageOnNoResults ?? ''}
            outline={keyboardNavigated}
            isButton={isButton || false}
            selectedValues={props.multiSelect ? props.value || [] : [props.value || '']}
            setSelectedValues={handleValueSelect}
            id={`${id}_dropdowncontent`}
          />
        )}
      </DropdownContainer>
      {activeValidationMessage && (
        <ErrorMessage>
          <SystemIcons.TechnicalWarning color={COLORS.critical_400}/>
          <span>{activeValidationMessage}</span>
        </ErrorMessage>
      )}
      {autofilledMessage && (
        <AutofilledMessage>
          <SystemIcons.Information color={COLORS.neutral_600}/>
          <span>{autofilledMessage}</span>
        </AutofilledMessage>
      )}
    </>
  );
});

export default BasicDropdown;
